import React from "react";
import { Box } from "@mui/material";
import { Typography } from "@mui/joy";
import { SxProps, Theme } from "@mui/system";
import {WidgetBox} from "@dreebit-registry/vsm-microservice-components";

interface Props {
    /** Card title (rendered as a titleMedium when a string). */
    title: React.ReactNode;
    /** Header right slot — typically one or more CardTag chips. */
    tags?: React.ReactNode;
    /** Card body. */
    children?: React.ReactNode;
    /** Footer left slot — audit/meta text (rendered as body-sm neutral when a string). */
    footer?: React.ReactNode;
    /** Footer right slot — action buttons. */
    actions?: React.ReactNode;
    /** Highlights the card with a colored outline. */
    highlighted?: boolean;
    sx?: SxProps<Theme>;
}

/**
 * Generic content card: a title with optional header tags, a body, and an
 * optional footer carrying meta text (left) and actions (right). Built on
 * WidgetBox; used by Service Reminders, Notification Subscriptions, …
 */
const EntityCard = ({ title, tags, children, footer, actions, highlighted, sx }: Props) => {
    const hasFooter = Boolean(footer || actions);

    return (
        <WidgetBox
            sx={[
                {
                    gap: "12px",
                    outline: highlighted ? "2px solid" : "none",
                    outlineColor: "primary.500",
                    transition: "outline-color 0.2s",
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                {typeof title === "string" ? (
                    <Typography level="titleMedium" sx={{ color: "#333" }}>
                        {title}
                    </Typography>
                ) : (
                    title
                )}
                {tags && (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {tags}
                    </Box>
                )}
            </Box>

            {children}

            {hasFooter && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    sx={{ borderTop: "1px solid #eee", pt: "12px", mt: "4px" }}
                >
                    {typeof footer === "string" ? (
                        <Typography level="body-sm" color="neutral" noWrap sx={{ minWidth: 0 }}>
                            {footer}
                        </Typography>
                    ) : (
                        footer ?? <span />
                    )}
                    {actions && (
                        <Box display="flex" gap={1} sx={{ flexShrink: 0 }}>
                            {actions}
                        </Box>
                    )}
                </Box>
            )}
        </WidgetBox>
    );
};

export default EntityCard;
